import React, { useRef } from "react";
import { Box } from '@curves/ui-base';
import PropTypes from "prop-types";
import { Tablenization, MorePageBox, ScrollerArrowButton } from "@Local/Components";
import clsx from "clsx";
/**
 * OverviewListTable 是一個通用總覽列表容器，用於顯示資料列與分頁控制。
 * 支援標準分頁與瀑布流分頁模式（Masonry），並可搭配 ScrollerArrowButton 提供橫向捲動提示。
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - 外層容器的自定義 class
 * @param {Array<Object>} [props.list=[]] - 表格的資料列（rowsData）
 * @param {Array<Object>} props.headerRow - 表格的表頭設定，包含欄位鍵值、標題與格式化函式
 * @param {Function} [props.refresh] - 用於刷新資料列的函式，會傳給 TableRowBody
 * @param {Object} [props.bodyRowClass] - 設定某些 row 的 className 條件
 * @param {Function} [props.onChangePage] - 分頁時觸發的回呼函式
 * @param {number} [props.pageIndex] - 當前分頁頁碼（從 1 開始）
 * @param {number} [props.totalPage] - 總頁數，用於控制分頁元件
 * @param {boolean} [props.isPagination=false] - 是否啟用標準分頁
 * @param {boolean} [props.isMasonryPagination=false] - 是否啟用瀑布流分頁（MorePageBox）
 * @param {boolean} [props.isGray=false] - 是否使用灰色主題樣式
 * @param {Object} [props.tableRowHeaderProps={}] - 傳入給 TableRowHeader 的額外 props
 * @param {Object} [props.tableRowBodyProps={}] - 傳入給 TableRowBody 的額外 props
 * @param {Object} [props.rest] - 其餘會傳遞到最外層 Box 的 props（例如 style 等）
 *
 * @returns {JSX.Element} 表格列表元件，包含表頭、資料列、分頁控制與捲動箭頭
 */
const OverviewListTable = ({
	className,
	list,
	headerRow,
	refresh,
	bodyRowClass,
	onChangePage,
	pageIndex,
	totalPage,
	isPagination = false,
	isMasonryPagination = false,
	isGray = false,
	tableRowHeaderProps = {},
	tableRowBodyProps = {},
	...rest
}) => {
	const scrollerArrowRef = useRef(null);
	return (
		<Box className={clsx("scroll-arrow-container", className)} {...rest}>
			<Tablenization
				cardProps={{ className: clsx("overview-list__table", { "theme-gray": isGray }) }}
				page={+pageIndex}
				count={totalPage}
				onChange={onChangePage}
				tableContainerRef={scrollerArrowRef}
				isPagination={isPagination || isMasonryPagination}
				Pagination={isMasonryPagination && MorePageBox}
			>
				<Tablenization.TableRowHeader headerRow={headerRow || []} {...tableRowHeaderProps} />
				<Tablenization.TableRowBody headerRow={headerRow || []} rowsData={list || []} refresh={refresh} bodyRowClass={bodyRowClass} {...tableRowBodyProps} />
			</Tablenization>
			<ScrollerArrowButton parentRef={scrollerArrowRef} />
		</Box>
	);
};
OverviewListTable.propTypes = {
	className: PropTypes.string,
	list: PropTypes.array,
	headerRow: PropTypes.arrayOf(
		PropTypes.shape({
			cellKey: PropTypes.string.isRequired,
			headerLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
			formatCell: PropTypes.func,
			align: PropTypes.string,
		})
	).isRequired,
	refresh: PropTypes.func,
	bodyRowClass: PropTypes.shape({
		key: PropTypes.string.isRequired,
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
		className: PropTypes.string.isRequired,
	}),
	onChangePage: PropTypes.func,
	pageIndex: PropTypes.number,
	totalPage: PropTypes.number,
	isPagination: PropTypes.bool,
	isMasonryPagination: PropTypes.bool,
	isGray: PropTypes.bool,
};
export default OverviewListTable;
